import { compact } from 'lodash'
import moment from 'moment'
import { useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import { openLocationUsageDetail } from 'app/components/usage-tracking/LocationUsageDetailModal'
import {
  OPS_USAGE_COST_METRICS_QUERY,
  type UsageCostLocationRow,
  type UsageCostQueryData,
} from 'app/components/usage-tracking/graphql'
import type { UsageGranularity, UsageTimeSeriesBucket } from 'app/components/usage-tracking/UsageCostChart'

export type UsageProductTab = `All` | `Voice Agent` | `SMS Agent`
export type UsageView = `summary` | `locations`

const DATE_FORMAT = `YYYY-MM-DD`
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

// opsUsageCost* live on the ops schema (/graphql/ops); route these urql queries
// there. A stable module-level context object avoids re-running the query on
// every render.
const OPS_CONTEXT = { url: OPS_URL }

export type UsageCountRow = { key: string; count: number }

export type UseUsageTrackingReturn = {
  openTab: UsageProductTab
  view: UsageView
  granularity: UsageGranularity
  fromDate: string
  toDate: string
  isLoading: boolean
  summary: UsageCostQueryData[`summary`] | undefined
  timeSeries: UsageTimeSeriesBucket[]
  filteredLocationRows: UsageCostLocationRow[]
  voiceOutcomes: UsageCountRow[]
  voiceTransferredCalls: number
  smsToolCalls: UsageCountRow[]
  avgSmsLatencyMs: number | undefined
  errorMessage: string | undefined
  handleTabChange: (tab: UsageProductTab) => void
  handleViewChange: (view: UsageView) => void
  handleGranularityChange: (granularity: UsageGranularity) => void
  handleDateChange: (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => void
  setLocationSearchQuery: (query: string) => void
}

function isValidTab(value: string | null): value is UsageProductTab {
  return value === `All` || value === `Voice Agent` || value === `SMS Agent`
}

function isValidView(value: string | null): value is UsageView {
  return value === `summary` || value === `locations`
}

function isValidGranularity(value: string | null): value is UsageGranularity {
  return value === `hourly` || value === `daily` || value === `weekly` || value === `monthly`
}

function isValidDateString(value: string | null): value is string {
  return !!value && DATE_REGEX.test(value) && moment(value, DATE_FORMAT, true).isValid()
}

function defaultFromDate(): string {
  return moment().subtract(30, `days`).format(DATE_FORMAT)
}

function defaultToDate(): string {
  return moment().format(DATE_FORMAT)
}

function resolveInitialDateRange(searchParams: URLSearchParams): { fromDate: string; toDate: string } {
  const rawFromDate = searchParams.get(`fromDate`)
  const rawToDate = searchParams.get(`toDate`)
  if (isValidDateString(rawFromDate) && isValidDateString(rawToDate) && rawFromDate <= rawToDate) {
    return { fromDate: rawFromDate, toDate: rawToDate }
  }
  return { fromDate: defaultFromDate(), toDate: defaultToDate() }
}

function tabToProduct(tab: UsageProductTab): `all` | `voice` | `sms` {
  if (tab === `Voice Agent`) return `voice`
  if (tab === `SMS Agent`) return `sms`
  return `all`
}

export function useUsageTracking(): UseUsageTrackingReturn {
  const [searchParams, setSearchParams] = useSearchParams()

  const [openTab, setOpenTab] = useState<UsageProductTab>(() => {
    const rawTab = searchParams.get(`tab`)
    return isValidTab(rawTab) ? rawTab : `All`
  })
  const [view, setView] = useState<UsageView>(() => {
    const rawView = searchParams.get(`view`)
    return isValidView(rawView) ? rawView : `summary`
  })
  const [granularity, setGranularity] = useState<UsageGranularity>(() => {
    const rawGranularity = searchParams.get(`granularity`)
    return isValidGranularity(rawGranularity) ? rawGranularity : `daily`
  })
  const [dateRange, setDateRange] = useState(() => resolveInitialDateRange(searchParams))
  const { fromDate, toDate } = dateRange

  const locationIdsParam = searchParams.get(`locationIds`)
  const locationIds = useMemo(() => compact(locationIdsParam?.split(`,`) ?? []), [locationIdsParam])

  const [locationSearchQuery, setLocationSearchQuery] = useState<string>(``)

  const [{ data, fetching, error }] = useQuery({
    query: OPS_USAGE_COST_METRICS_QUERY,
    context: OPS_CONTEXT,
    variables: {
      input: {
        dateRange: { fromDate, toDate },
        granularity,
        product: tabToProduct(openTab),
        ...(locationIds.length > 0 && { locationIds }),
      },
    },
  })

  const summary = data?.opsUsageCostMetrics.summary

  const timeSeries: UsageTimeSeriesBucket[] = useMemo(
    () =>
      (data?.opsUsageCostMetrics.buckets ?? []).map((bucket) => ({
        bucketStart: bucket.date,
        voiceCostUsd: bucket.voiceCostUsd,
        smsCostUsd: bucket.smsCostUsd,
      })),
    [data],
  )

  const filteredLocationRows: UsageCostLocationRow[] = useMemo(() => {
    const locationRows = data?.opsUsageCostMetrics.locations ?? []
    if (!locationSearchQuery) return locationRows
    const query = locationSearchQuery.toLowerCase()
    return locationRows.filter(
      (row) =>
        (row.locationName ?? ``).toLowerCase().includes(query) ||
        (row.workspaceName ?? ``).toLowerCase().includes(query),
    )
  }, [data, locationSearchQuery])

  // Deep link: `?locationDetail=<id>` (what a cmd-click on a Location Name cell opens)
  // reopens the drill-down once the rows it needs have loaded.
  const locationRows = data?.opsUsageCostMetrics.locations
  useEffect(() => {
    const locationDetailId = searchParams.get(`locationDetail`)
    if (!locationDetailId || !locationRows?.length) return
    // Clear the param immediately so the effect cannot re-fire on the next render.
    setSearchParams((prev) => {
      prev.delete(`locationDetail`)
      return prev
    })
    const row = locationRows.find((candidate) => candidate.locationId === locationDetailId)
    if (row) openLocationUsageDetail(row, fromDate, toDate)
  }, [locationRows, searchParams.get(`locationDetail`), fromDate, toDate])

  const voiceOutcomes = data?.opsUsageCostMetrics.voiceOutcomes ?? []
  const voiceTransferredCalls = data?.opsUsageCostMetrics.voiceTransferredCalls ?? 0
  const smsToolCalls = data?.opsUsageCostMetrics.smsToolCalls ?? []
  const avgSmsLatencyMs = data?.opsUsageCostMetrics.avgSmsLatencyMs ?? undefined

  const isLoading = fetching && !data
  const errorMessage = error?.message

  const handleTabChange = (nextTab: UsageProductTab) => {
    setOpenTab(nextTab)
    setSearchParams((prev) => {
      prev.set(`tab`, nextTab)
      return prev
    })
  }

  const handleViewChange = (nextView: UsageView) => {
    setView(nextView)
    setSearchParams((prev) => {
      prev.set(`view`, nextView)
      return prev
    })
  }

  const handleGranularityChange = (nextGranularity: UsageGranularity) => {
    setGranularity(nextGranularity)
    setSearchParams((prev) => {
      prev.set(`granularity`, nextGranularity)
      return prev
    })
  }

  // DashboardHeader's dateChangeHandler signature includes compare params;
  // we only use the primary range.
  const handleDateChange = (nextFromDate: string, nextToDate: string) => {
    setDateRange({ fromDate: nextFromDate, toDate: nextToDate })
  }

  return {
    openTab,
    view,
    granularity,
    fromDate,
    toDate,
    isLoading,
    summary,
    timeSeries,
    filteredLocationRows,
    voiceOutcomes,
    voiceTransferredCalls,
    smsToolCalls,
    avgSmsLatencyMs,
    errorMessage,
    handleTabChange,
    handleViewChange,
    handleGranularityChange,
    handleDateChange,
    setLocationSearchQuery,
  }
}
